import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import { MdOutlineClear } from "react-icons/md";
import { JReports, setReportSearch } from "../../../../../app/slices/jomla/reportsSlice";
import { restoreFilters } from "../../../../../helpers/restoreFilters";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import dayjs from "dayjs";

const JRevenueTable = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.jReports.jRevenue);
  const loading = useSelector((state) => state.jReports.loading);
  const reportTypes = useSelector((state) => state.jReports.reportTypes);

  const _SearchBody = useSelector((state) => state.jReports.searchBody.revenue);
  const [search, setSearch] = useState(() =>
    restoreFilters({
    startDate: dayjs().format("YYYY-MM-01"),
    endDate: dayjs().format("YYYY-MM-DD"),
  }, _SearchBody)
  );
  useEffect(() => {
    dispatch(setReportSearch({ name: "revenue", body: search }));
  }, [dispatch, search]);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "number",
      headerName: "رقم الطلب",
      flex: 0.8,
    },
    {
      field: "pos_number",
      headerName: "POS",
      flex: 0.8,
    },
    {
      field: "pos_name",
      headerName: "اسم النقطة",
      flex: 1.2,
    },
    {
      field: "items_count",
      headerName: "عدد المواد",
      flex: 0.8,
      renderCell: (params) => {
        return (
          <Typography>{params.row.items_count?.toLocaleString()}</Typography>
        );
      },
    },
    {
      field: "created_at",
      headerName: "تاريخ الإنشاء",
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack>
            <Typography>
              {dayjs(params.row.created_at).format("YYYY-MM-DD")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {dayjs(params.row.created_at).format("hh:mm A")}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "revenue",
      headerName: "الربح (د.ع)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography color={params.row.revenue >= 0 ? "green" : "error"}>
            {params.row.revenue?.toLocaleString()}
          </Typography>
        );
      },
    },
  ];

  useEffect(() => {
    if (reportTypes[7]?.isSelected) {
      dispatch(
        JReports.jRevenue({
          start_date: search.startDate,
          end_date: search.endDate,
        })
      );
    }
  }, [dispatch, reportTypes, search]);

  const customToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport
        csvOptions={{
          fileName: "تقرير الأرباح | نظام دجلة",
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          {data && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                        color: "#1976d2",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(25, 118, 210, 0.15)",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_orders_count?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          عدد الطلبات
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                        color: "#7b1fa2",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(123, 31, 162, 0.15)",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_orders_amount?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          مجموع الطلبات (د.ع)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)",
                        color: "#388e3c",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(56, 142, 60, 0.15)",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_order_discount?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          الخصومات (د.ع)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          "linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)",
                        color: "#f57c00",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(245, 124, 0, 0.15)",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_spending?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          المصروفات (د.ع)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                        color: "#00695c",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(0, 105, 92, 0.15)",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_orders_revenue?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          الإيرادات (د.ع)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        background:
                          data.j_net_revenue >= 0
                            ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                            : "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                        color: data.j_net_revenue >= 0 ? "#2e7d32" : "#c62828",
                        height: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow:
                          data.j_net_revenue >= 0
                            ? "0 6px 25px rgba(46, 125, 50, 0.2)"
                            : "0 6px 25px rgba(198, 40, 40, 0.2)",
                        border:
                          data.j_net_revenue >= 0
                            ? "2px solid #66bb6a"
                            : "2px solid #ef5350",
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {data.j_net_revenue?.toLocaleString()}
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8 }}>
                          صافي الأرباح (د.ع)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <DateRangepicker
              startDate={search.startDate}
              endDate={search.endDate}
              handleOnChange={(searchDate) => {
                const updatedFilter = {
                  from: dayjs(searchDate[0].startDate).format("YYYY-MM-DD"),
                  to: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                };
                if (
                  updatedFilter.from !== searchDate.startDate ||
                  updatedFilter.to !== searchDate.endDate
                ) {
                  setSearch({
                    ...search,
                    startDate: dayjs(searchDate[0].startDate).format(
                      "YYYY-MM-DD"
                    ),
                    endDate: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs="auto" md={1}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch({
                    startDate: dayjs().format("YYYY-MM-01"),
                    endDate: dayjs().format("YYYY-MM-DD"),
                  });
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <ResponsiveDataGrid gridHeight={null}
              rows={
                data?.j_orders?.map((x, index) => ({
                  ...x,
                  index: index + 1,
                })) ?? []
              }
              autoHeight
              loading={loading}
              columns={columns}
              rowHeight={40}
              components={{
                LoadingOverlay: LinearProgress,
                Toolbar: customToolbar,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JRevenueTable;
